import { registerBlockType } from '@wordpress/blocks';
import { useBlockProps, InnerBlocks, InspectorControls, MediaUpload, MediaUploadCheck } from '@wordpress/block-editor';
import { PanelBody, SelectControl, Button, ToggleControl } from '@wordpress/components';
import { __ } from '@wordpress/i18n';

registerBlockType('puroclean/content-banner', {
    edit({ attributes, setAttributes }) {
        const { 
            bannerImage, 
            overlayColor, 
            overlayOpacity, 
            contentAlignment, 
            showDividingLines 
        } = attributes;
        
        const blockProps = useBlockProps({
            className: 'content-banner'
        });

        // Get the overlay color class
        const getOverlayColorClass = (color) => {
            switch (color) {
                case 'secondary':
                    return 'bg-secondary';
                case 'primary':
                    return 'bg-primary';
                case 'gray-900':
                    return 'bg-gray-900';
                default:
                    return 'bg-transparent';
            }
        };

        // Get the overlay opacity class
        const getOverlayOpacityClass = (opacity) => {
            switch (opacity) {
                case '25':
                    return 'bg-opacity-25';
                case '50':
                    return 'bg-opacity-50';
                case '75':
                    return 'bg-opacity-75';
                case '90':
                    return 'bg-opacity-90';
                default:
                    return 'bg-opacity-75';
            }
        };

        // Get the content alignment class
        const getContentAlignmentClass = (alignment) => {
            switch (alignment) {
                case 'left':
                    return 'text-left';
                case 'right':
                    return 'text-right';
                default:
                    return 'text-center';
            }
        };

        // Template for inner blocks - allow common content blocks
        const ALLOWED_BLOCKS = [
            'core/paragraph',
            'core/heading', 
            'core/button',
            'core/list',
            'core/quote',
            'core/separator',
            'core/spacer',
            'core/group'
        ];

        const TEMPLATE = [
            ['core/heading', { 
                level: 2, 
                placeholder: 'Enter your heading...',
                className: 'text-white text-4xl font-bold mb-6'
            }],
            ['core/paragraph', { 
                placeholder: 'Add your content here...',
                className: 'text-white text-lg mb-4'
            }]
        ];

        const overlayColorClass = getOverlayColorClass(overlayColor);
        const overlayOpacityClass = getOverlayOpacityClass(overlayOpacity);
        const contentAlignmentClass = getContentAlignmentClass(contentAlignment);

        return (
            <>
                <InspectorControls>
                    <PanelBody title={__('Banner Settings', 'puroclean')} initialOpen={true}>
                        <MediaUploadCheck>
                            <MediaUpload
                                onSelect={(media) => setAttributes({ 
                                    bannerImage: {
                                        url: media.url,
                                        id: media.id,
                                        alt: media.alt || 'Banner Background'
                                    }
                                })}
                                allowedTypes={['image']}
                                value={bannerImage?.id}
                                render={({ open }) => (
                                    <div>
                                        <Button
                                            onClick={open}
                                            variant="secondary"
                                            className="mb-4"
                                        >
                                            {bannerImage?.url ? __('Change Background Image', 'puroclean') : __('Select Background Image', 'puroclean')}
                                        </Button>
                                        {bannerImage?.url && (
                                            <div className="mb-4">
                                                <img 
                                                    src={bannerImage.url} 
                                                    alt={bannerImage.alt}
                                                    className="w-full h-24 object-cover rounded"
                                                />
                                                <Button
                                                    onClick={() => setAttributes({ bannerImage: { url: '', id: 0, alt: '' } })}
                                                    variant="link"
                                                    className="text-red-500 mt-2"
                                                >
                                                    {__('Remove Image', 'puroclean')}
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                )}
                            />
                        </MediaUploadCheck>

                        <SelectControl
                            label={__('Overlay Color', 'puroclean')}
                            value={overlayColor}
                            options={[
                                { label: __('Secondary Blue', 'puroclean'), value: 'secondary' },
                                { label: __('Primary Red', 'puroclean'), value: 'primary' },
                                { label: __('Dark Gray', 'puroclean'), value: 'gray-900' },
                                { label: __('Transparent', 'puroclean'), value: 'transparent' }
                            ]}
                            onChange={(value) => setAttributes({ overlayColor: value })}
                        />

                        <SelectControl
                            label={__('Overlay Opacity', 'puroclean')}
                            value={overlayOpacity}
                            options={[
                                { label: __('25%', 'puroclean'), value: '25' },
                                { label: __('50%', 'puroclean'), value: '50' },
                                { label: __('75%', 'puroclean'), value: '75' },
                                { label: __('90%', 'puroclean'), value: '90' }
                            ]}
                            onChange={(value) => setAttributes({ overlayOpacity: value })}
                        />

                        <SelectControl
                            label={__('Content Alignment', 'puroclean')}
                            value={contentAlignment}
                            options={[
                                { label: __('Left', 'puroclean'), value: 'left' },
                                { label: __('Center', 'puroclean'), value: 'center' },
                                { label: __('Right', 'puroclean'), value: 'right' }
                            ]}
                            onChange={(value) => setAttributes({ contentAlignment: value })}
                        />

                        <ToggleControl
                            label={__('Show Dividing Lines', 'puroclean')}
                            checked={showDividingLines}
                            onChange={(value) => setAttributes({ showDividingLines: value })}
                        />
                    </PanelBody>
                </InspectorControls>

                <div {...blockProps}>
                    {/* Banner with Background Image */}
                    <div className="relative min-h-[400px] flex items-center">
                        {/* Background Image */}
                        {bannerImage?.url && (
                            <div 
                                className="absolute inset-0 bg-cover bg-center bg-no-repeat"
                                style={{ backgroundImage: `url(${bannerImage.url})` }}
                            />
                        )}
                        
                        {/* Overlay */}
                        {overlayColor !== 'transparent' && (
                            <div className={`absolute inset-0 ${overlayColorClass} ${overlayOpacityClass}`} />
                        )}
                        
                        {/* Content */}
                        <div className="relative z-10 w-full py-16">
                            <div className="max-w-content mx-auto px-4">
                                <div className={contentAlignmentClass}>
                                    <InnerBlocks
                                        allowedBlocks={ALLOWED_BLOCKS}
                                        template={TEMPLATE}
                                        templateLock={false}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    {/* Dividing Lines (reused from trust-factors) */}
                    {showDividingLines && (
                        <>
                            <div className="w-full h-px bg-white"></div>
                            <div className="flex w-full h-4">
                                <div className="flex-1 bg-secondary"></div>
                                <div className="flex-1 bg-primary"></div>
                                <div className="flex-1 bg-[#373536]"></div>
                                <div className="flex-1 bg-[#A7A9AC]"></div>
                            </div>
                        </>
                    )}
                </div>
            </>
        );
    },
    save() {
        return <InnerBlocks.Content />;
    }
});
